import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
type ServiceChoice = 'flowers' | 'balloons' | 'both';
type Status = 'idle' | 'sending' | 'sent';
type Fields = {
  name: string;
  email: string;
  eventType: string;
  service: ServiceChoice;
  date: string;
  message: string;
};
type Errors = Partial<Record<'name' | 'email' | 'eventType' | 'message', string>>;
const empty: Fields = {
  name: '',
  email: '',
  eventType: '',
  service: 'both',
  date: '',
  message: ''
};
const inputBase = 'mt-2 h-12 w-full rounded-lg border bg-white px-4 text-sm text-ink outline-none transition-colors duration-150 ease-smooth focus:border-mauve focus:ring-2 focus:ring-mauve/20';
export function ContactForm() {
  const {
    t
  } = useLanguage();
  const f = t.contact.form;
  const [fields, setFields] = useState<Fields>(empty);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const set = <K extends keyof Fields,>(key: K, value: Fields[K]) => {
    setFields(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
    if (status === 'sent') setStatus('idle');
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!fields.name.trim()) next.name = f.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) next.email = f.errors.email;
    if (!fields.eventType) next.eventType = f.errors.eventType;
    if (!fields.message.trim()) next.message = f.errors.message;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('sending');
    await new Promise(r => setTimeout(r, 900));
    setStatus('sent');
    setFields(empty);
  };
  const border = (key: keyof Errors) => errors[key] ? 'border-mauve' : 'border-petal';
  return <form onSubmit={onSubmit} noValidate className="rounded-2xl bg-cream p-6 sm:p-10" data-id="element-234" data-mp="yz0luz0ep79x">
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-235" data-mp="z0uylq1ixt2x">
        <div data-id="element-236" data-mp="fvgmx1nrfarm">
          <Label htmlFor="cf-name" text={f.name} required={f.required} data-id="element-237" data-mp="fvyh7sqv3eww" />
          <input id="cf-name" value={fields.name} onChange={e => set('name', e.target.value)} autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'cf-name-error' : undefined} className={`${inputBase} ${border('name')}`} data-id="element-238" data-mp="b3f8pa7179yy" />
          <ErrorText id="cf-name-error" text={errors.name} data-id="element-239" data-mp="yqx0v6u7ka2g" />
        </div>
        <div data-id="element-240" data-mp="tupxnlt75co7">
          <Label htmlFor="cf-email" text={f.email} required={f.required} data-id="element-241" data-mp="ts3m7b36qd0p" />
          <input id="cf-email" type="email" value={fields.email} onChange={e => set('email', e.target.value)} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'cf-email-error' : undefined} className={`${inputBase} ${border('email')}`} data-id="element-242" data-mp="otillak0trbq" />
          <ErrorText id="cf-email-error" text={errors.email} data-id="element-243" data-mp="1k58hprn7m66f" />
        </div>
        <div data-id="element-244" data-mp="1p2qqni188lef">
          <Label htmlFor="cf-type" text={f.eventType} required={f.required} data-id="element-245" data-mp="1p4bll8rge6ct" />
          <select id="cf-type" value={fields.eventType} onChange={e => set('eventType', e.target.value)} aria-invalid={Boolean(errors.eventType)} aria-describedby={errors.eventType ? 'cf-type-error' : undefined} className={`${inputBase} ${border('eventType')} ${fields.eventType ? '' : 'text-ink/50'}`} data-id="element-246" data-mp="1u1uya48i6b2n">
            <option value="" disabled data-id="element-247" data-mp="2ct48ygu0f5vt">
              {f.selectPlaceholder}
            </option>
            {Object.entries(f.eventTypes).map(([value, label]) => <option key={value} value={value} className="text-ink" data-id="element-248" data-mp="2hr607p1labid">
                {label}
              </option>)}
          </select>
          <ErrorText id="cf-type-error" text={errors.eventType} data-id="element-249" data-mp="nden2fk1x9i6" />
        </div>
        <div data-id="element-250" data-mp="2dv1r3wlujkdw">
          <Label htmlFor="cf-date" text={f.date} data-id="element-251" data-mp="2duifsyqpbu8u" />
          <input id="cf-date" type="date" value={fields.date} onChange={e => set('date', e.target.value)} className={`${inputBase} border-petal`} data-id="element-252" data-mp="2dt74mduxfk7p" />
        </div>

        <fieldset className="sm:col-span-2" data-id="element-253" data-mp="2iueau8hf8bc9">
          <legend className="text-xs font-medium uppercase tracking-wider2 text-ink/55" data-id="element-254" data-mp="2ivfuv2gf0864">{f.service}</legend>
          <div className="mt-2 grid grid-cols-3 gap-2 rounded-full bg-white p-1 ring-1 ring-petal" data-id="element-255" data-mp="1v526pn0yatgt">
            {(Object.keys(f.services) as ServiceChoice[]).map(key => {
            const active = fields.service === key;
            return <label key={key} className={`flex h-10 cursor-pointer items-center justify-center rounded-full text-sm font-medium transition-colors duration-150 ease-smooth has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-mauve ${active ? 'bg-mauve text-white' : 'text-ink/70 hover:bg-petal/60'}`} data-id="element-256" data-mp="1z5m0qhq89gyq">
                  <input type="radio" name="service" value={key} checked={active} onChange={() => set('service', key)} className="sr-only" data-id="element-257" data-mp="1229fd1937qe9" />
                  {f.services[key]}
                </label>;
          })}
          </div>
        </fieldset>

        <div className="sm:col-span-2" data-id="element-258" data-mp="1l158e997lyqi">
          <Label htmlFor="cf-message" text={f.message} required={f.required} data-id="element-259" data-mp="1l03kv5b0bjrw" />
          <textarea id="cf-message" rows={5} value={fields.message} onChange={e => set('message', e.target.value)} placeholder={f.messagePlaceholder} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'cf-message-error' : undefined} className={`mt-2 w-full resize-y rounded-lg border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-150 ease-smooth focus:border-mauve focus:ring-2 focus:ring-mauve/20 ${border('message')}`} data-id="element-260" data-mp="162b7ojsxn7gv" />
          <ErrorText id="cf-message-error" text={errors.message} data-id="element-261" data-mp="1sjctgwbovefw" />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-262" data-mp="1nljog6m4gqh7">
        <button type="submit" disabled={status === 'sending'} className="inline-flex h-12 items-center gap-2 rounded-full bg-mauve px-8 text-sm font-medium text-white transition-colors duration-150 ease-smooth hover:bg-mauve-deep disabled:cursor-not-allowed disabled:opacity-70" data-id="element-263" data-mp="1nixey1fo80wc">
          {status === 'sending' ? <>
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-264" data-mp="14nqmp7n8imle" />
              {f.sending}
            </> : f.submit}
        </button>
        <p className="text-xs text-ink/60" data-id="element-265" data-mp="2z1i3qs7w88qv">{f.hint}</p>
      </div>

      <div aria-live="polite" data-id="element-266" data-mp="36jzjvha9ylst">
        {status === 'sent' ? <p className="mt-6 flex items-start gap-2 rounded-lg bg-petal px-4 py-3 text-sm text-mauve-deep" data-id="element-267" data-mp="36hmzyykyi1d4">
            <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-268" data-mp="3bgg5o4u04o2q" />
            {f.success}
          </p> : null}
      </div>
    </form>;
}
function Label({
  htmlFor,
  text,
  required
}: {
  htmlFor: string;
  text: string;
  required?: string;
}) {
  return <label htmlFor={htmlFor} className="block text-xs font-medium uppercase tracking-wider2 text-ink/55" data-id="element-269" data-mp="2nmogplliv883">
      {text}
      {required ? <span className="text-mauve" aria-label={required} data-id="element-270" data-mp="2xjb0s860xv92">
          {' '}
          *
        </span> : null}
    </label>;
}
function ErrorText({
  id,
  text
}: {
  id: string;
  text?: string;
}) {
  if (!text) return null;
  return <p id={id} className="mt-1.5 text-xs font-medium text-mauve" data-id="element-271" data-mp="cs0uieeaz6mq">
      {text}
    </p>;
}